import React, { useState, useEffect } from "react";
import { ToggleButton, ButtonGroup, Button, Form } from "react-bootstrap";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { TbReportAnalytics } from "react-icons/tb";
import { BsDownload } from "react-icons/bs";
import DatePicker from "react-datepicker";
import { he } from "date-fns/locale";
import "../../../../styles/react-datepicker.css";

function ReportsPane({
  groups,
  students,
  presenceLogs,
  events,
  evaluationReports,
  evaluationTemplates,
}) {
  // Use the original data arrays with safety checks
  const finalGroups = groups || [];
  const finalStudents = students || [];
  const finalPresenceLogs = presenceLogs || [];
  // States
  const [reportType, setReportType] = useState("1");
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [reportPeriod, setReportPeriod] = useState("1"); // New state for report period
  const [selectedTemplate, setSelectedTemplate] = useState(null); // New state for selected template

  // New states for presence report date selection
  const [presenceDateMode, setPresenceDateMode] = useState("lastMonth"); // "lastMonth" or "customRange"
  const [presenceStartDate, setPresenceStartDate] = useState(null);
  const [presenceEndDate, setPresenceEndDate] = useState(null);

  // New states for presence report group selection
  const [selectedDojo, setSelectedDojo] = useState("");
  const [selectedGroups, setSelectedGroups] = useState([]);

  // Filter students based on search query
  const filteredStudents = finalStudents.filter((student) =>
    student?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get unique dojos from groups
  const uniqueDojos = [
    ...new Set(finalGroups.map((group) => group.dojo)),
  ].sort();

  // Get groups filtered by selected dojo
  const groupsInSelectedDojo = finalGroups.filter(
    (group) => group.dojo === selectedDojo
  );

  // Set default template when evaluationTemplates changes
  useEffect(() => {
    if (
      evaluationTemplates &&
      evaluationTemplates.length > 0 &&
      !selectedTemplate
    ) {
      // Find the latest non-archived template by creation date
      const nonArchivedTemplates = evaluationTemplates.filter(
        (template) => !template.isArchived
      );

      if (nonArchivedTemplates.length > 0) {
        const latestTemplate = nonArchivedTemplates.reduce(
          (latest, current) => {
            const latestTime = latest.createdAt?.seconds || 0;
            const currentTime = current.createdAt?.seconds || 0;
            return currentTime > latestTime ? current : latest;
          }
        );
        setSelectedTemplate(latestTemplate);
      }
    }
  }, [evaluationTemplates, selectedTemplate]);

  const loadHebrewFonts = async (doc) => {
    // Helper function to convert ArrayBuffer to Base64
    const arrayBufferToBase64 = (buffer) => {
      let binary = "";
      const bytes = new Uint8Array(buffer);
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      return btoa(binary);
    };

    // Load Regular Font
    const regularResponse = await fetch("/DavidLibre-Regular.ttf");
    const regularFontData = await regularResponse.arrayBuffer();
    const regularBase64 = arrayBufferToBase64(regularFontData);
    doc.addFileToVFS("DavidLibre-Regular.ttf", regularBase64);
    doc.addFont("DavidLibre-Regular.ttf", "DavidLibre", "normal");

    // Load Bold Font
    const boldResponse = await fetch("/DavidLibre-Bold.ttf");
    const boldFontData = await boldResponse.arrayBuffer();
    const boldBase64 = arrayBufferToBase64(boldFontData);
    doc.addFileToVFS("DavidLibre-Bold.ttf", boldBase64);
    doc.addFont("DavidLibre-Bold.ttf", "DavidLibre", "bold");
  };

  // Helper to reverse a string for LTR fields in RTL tables
  const reverseStr = (val) =>
    val ? String(val).split("").reverse().join("") : val;

  function generateTableRows(data, attributes) {
    // List of LTR fields (add more as needed)
    const ltrFields = [
      "IDnumber",
      "firstParentNumber",
      "firstParentEmailAddress",
      "secondParentNumber",
      "secondParentEmailAddress",
      "birthday",
      "studentCreatedAt",
      "age",
      "weightKG",
      "shirtSize",
      "pantsSize",
    ];
    return data.map((item) => {
      return attributes.map((attr) => {
        if (attr === "") {
          return "";
        }
        let value = item[attr];
        // If the field is in ltrFields, wrap with ltr and do not reverse
        if (ltrFields.includes(attr)) {
          return reverseStr(value);
        }
        // Check if the value is a string and contains only digits
        if (typeof value === "string" && /^\d+$/.test(value)) {
          return value.split("").reverse().join("");
        }
        if (typeof value === "number") {
          return String(value).split("").reverse().join("");
        }
        return value;
      });
    });
  }

  // This function adds data to the last column
  function addDataToLastColumn(data, valueFunction) {
    const updatedTable = data.map((row) => {
      return [valueFunction(row), ...row];
    });
    return updatedTable;
  }

  // This function replaces values in a specific column using a provided function
  function replaceValuesInColumn(data, columnIndex, valueFunction) {
    return data.map((row) =>
      row.map((value, index) =>
        index === columnIndex ? valueFunction(value) : value
      )
    );
  }

  // This function is used to check if a presence report of a student was within the selected period
  function isWithinSelectedPeriod(logTime) {
    if (!logTime || typeof logTime.seconds !== "number") {
      return false;
    }

    const timestampMs = logTime.seconds * 1000; // Convert seconds to milliseconds

    // For presence reports, use custom date range if selected
    if (reportType === "3" && presenceDateMode === "customRange") {
      if (!presenceStartDate || !presenceEndDate) {
        return false;
      }
      const startMs = presenceStartDate.getTime();
      const endMs = presenceEndDate.getTime();
      return timestampMs >= startMs && timestampMs <= endMs;
    }

    // For other reports, use the period-based logic
    const currentTime = Date.now();
    const periodInDays = {
      1: 30, // 1 month
      3: 90, // 3 months
      6: 180, // 6 months
      12: 365, // 12 months
    };
    const selectedPeriod = periodInDays[reportPeriod] || 30;
    const periodAgo = currentTime - selectedPeriod * 24 * 60 * 60 * 1000;

    return timestampMs >= periodAgo;
  }

  // This function is used to check if an evaluation report was within the selected period
  function isEvaluationWithinSelectedPeriod(reportTime) {
    if (!reportTime || typeof reportTime.seconds !== "number") {
      return false;
    }

    const timestampMs = reportTime.seconds * 1000; // Convert seconds to milliseconds
    const currentTime = Date.now();
    const periodInDays = {
      1: 30, // 1 month
      3: 90, // 3 months
      6: 180, // 6 months
      12: 365, // 12 months
    };
    const selectedPeriod = periodInDays[reportPeriod] || 30;
    const periodAgo = currentTime - selectedPeriod * 24 * 60 * 60 * 1000;

    return timestampMs >= periodAgo;
  }

  // This function extracts the hebrew name of the report out of the reports_radio object
  const generateReportTitle = (radio_value) => {
    return reports_radio.find((report) => report.value === radio_value)?.name;
  };

  // This function returns the description of each report
  const generateReportDescription = (radio_value) => {
    return reports_radio.find((report) => report.value === radio_value)
      ?.description;
  };

  // This function calls the corresponding report generation function of the current report type the user wants to render
  const generateCurrentReport = (radio_value) => {
    try {
      const selectedReport = reports_radio.find(
        (report) => report.value === radio_value
      );
      if (selectedReport && selectedReport.generationFunction) {
        selectedReport.generationFunction();
      }
    } catch (error) {
      console.error("Error generating report:", error);
      // You might want to show an error message to the user here
    }
  };

  // Function to generate the groups PDF
  const generateGroupsReport = async () => {
    const doc = new jsPDF();

    // Load and set the Hebrew font
    await loadHebrewFonts(doc);
    doc.setFont("DavidLibre", "bold");

    // Get current date and format it as dd/mm/yyyy hh:mm
    const now = new Date();
    const formattedDate = now.toLocaleString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    // Add the date under the title
    doc.setFont("DavidLibre", "normal"); // Regular for the date
    doc.setFontSize(12);
    doc.text(formattedDate, 180, 20, { align: "center" });

    // Set the document direction to RTL (for Hebrew text)
    doc.setR2L(true);

    // Title
    doc.setFont("DavidLibre", "bold");
    doc.setFontSize(18);
    doc.text("דוח קבוצות", 105, 20, { align: "center" });
    doc.setFont("DavidLibre", "normal"); // Regular for the body

    // Table data
    // This is the actual columns we want at the table
    const tableColumn = ["ימי אימון", "מספר ספורטאים", "תיאור", "סניף", "שם"];

    // We generate the rows, based on the groups data, and we extract only the relevant fields, corresponding order to the columns.
    // Pay attention that we also extract the id and traininSlots fields, although it does not have a column
    // This is used for value calculation later
    let tableRows = generateTableRows(finalGroups, [
      "description",
      "dojo",
      "name",
      "id",
      "trainingSlots",
    ]);

    // We dynamically add the data to the "מספר חניכים" column
    tableRows = addDataToLastColumn(tableRows, (row) => {
      let numOfStudentsInGroup = 0;

      finalStudents.forEach((student) => {
        // Add null checks and ensure groupsIDs exists and has elements
        if (
          student?.status === "active" &&
          student?.groupsIDs?.length > 0 &&
          row?.[3]
        ) {
          const rowGroupId = row[3]?.trim();
          // Check if student belongs to this group (any of their groups)
          const belongsToGroup = student.groupsIDs.some(
            (groupId) => groupId?.trim() === rowGroupId
          );
          if (belongsToGroup) {
            numOfStudentsInGroup++;
          }
        }
      });

      return numOfStudentsInGroup;
    });

    // We dynamically add the data to the "ימי אימון" column
    tableRows = addDataToLastColumn(tableRows, (row) => {
      // Add null check for trainingSlots
      if (!row?.[5] || !Array.isArray(row[5])) {
        return "-";
      }

      // Sort training slots by day number for consistent ordering
      const sortedSlots = [...row[5]].sort((a, b) => {
        const dayA = parseInt(a.day) || 0;
        const dayB = parseInt(b.day) || 0;
        return dayA - dayB;
      });

      const daysMap = {
        1: "ראשון",
        2: "שני",
        3: "שלישי",
        4: "רביעי",
        5: "חמישי",
        6: "שישי",
        7: "שבת",
      };

      return (
        sortedSlots
          .map((slot) => {
            if (!slot?.day || !slot?.range) {
              return "";
            }
            const dayName = daysMap[slot.day] || slot.day;
            return `יום ${dayName} ${slot.range}`;
          })
          .filter(Boolean)
          .join("\n") || "-"
      );
    });

    // Calculate total students using the same logic as HomePane
    const activeGroupsIDs = finalGroups.map((group) => group.id);
    const studentsInActiveGroups = finalStudents.filter(
      (student) =>
        student?.groupsIDs?.length > 0 &&
        activeGroupsIDs.includes(student.groupsIDs[0])
    ).length;

    // Stats Text
    doc.setFont("DavidLibre", "normal");
    doc.setFontSize(12);
    doc.text("מספר הקבוצות :", 180, 30, { align: "center" });
    doc.text("מספר הספורטאים :", 180, 35, { align: "center" });
    doc.setR2L(false);
    doc.text(`${finalGroups.length}`, 160, 30, { align: "center" });
    doc.text(`${studentsInActiveGroups}`, 160, 35, { align: "center" });

    // Change again the R2L status
    doc.setR2L(true);
    doc.setFont("DavidLibre", "normal"); // Regular for the body

    // The table
    autoTable(doc, {
      startY: 40,
      head: [tableColumn],
      body: tableRows,
      margin: { left: 20, right: 20 },
      tableWidth: "auto",
      styles: {
        font: "DavidLibre",
        fontSize: 12,
        halign: "right",
        direction: "rtl",
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.001,
      },
      theme: "grid",
      headStyles: {
        fillColor: [232, 232, 232],
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        fontSize: 11,
        cellPadding: 1,
        minCellHeight: 8,
        valign: "middle",
      },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 25, halign: "right", direction: "ltr" },
        2: { cellWidth: 40 },
        3: { cellWidth: 25 },
        4: { cellWidth: 35 },
      },
      didParseCell: function (data) {
        if (data.section === "head") {
          data.cell.styles.cellPadding = 1;
          data.cell.styles.minCellHeight = 8;
        } else if (data.column.index === 0) {
          data.cell.styles.minCellHeight = 8;
          data.cell.styles.cellPadding = 3;
        }
      },
    });

    // Generate and store PDF
    try {
      const pdfBlob = doc.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);

      setPdfBlob(pdfBlob);
      setPdfUrl(pdfUrl);
    } catch (error) {
      console.error("Error generating groups report PDF:", error);
      // You might want to show an error message to the user here
    }
  };

  // Function to generate the groups PDF
  const generateStudentsReport = async () => {
    const doc = new jsPDF();

    // Load and set the Hebrew font
    await loadHebrewFonts(doc);
    doc.setFont("DavidLibre", "bold");

    // Get current date and format it as dd/mm/yyyy hh:mm
    const now = new Date();
    const formattedDate = now.toLocaleString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    // Add the date under the title
    doc.setFont("DavidLibre", "normal");
    doc.setFontSize(12);
    doc.text(formattedDate, 180, 20, { align: "center" });

    // Set the document direction to RTL (for Hebrew text)
    doc.setR2L(true);

    // Title
    doc.setFont("DavidLibre", "bold");
    doc.setFontSize(18);
    doc.text("דוח ספורטאים", 105, 20, { align: "center" });
    doc.setFont("DavidLibre", "normal");

    // Table data
    // This is the actual columns we want at the table
    const tableColumn = [
      "אחוז נוכחות *",
      "מספר טלפון הורה",
      "שם הורה",
      "קבוצות אימון",
      "כתובת",
      "גיל",
      "מין",
      "שם",
    ];

    // Filter students to only include those with required fields
    const validStudents = finalStudents.filter(
      (student) =>
        student?.name &&
        student?.firstParentName &&
        student?.firstParentEmailAddress
    );

    // Sort students by Hebrew name
    const sortedStudents = [...validStudents].sort((a, b) => {
      return (a?.name || "").localeCompare(b?.name || "", "he");
    });

    // We generate the rows
    // Pay attention, we make sure that each column has a corresponding object attribute that gets inserted
    // There are special columns that require further modifying (like אחוז נוכחות and קבוצות אימון), thus we fill in a relevant field to help us compute it later.
    let tableRows = generateTableRows(sortedStudents, [
      "id",
      "firstParentNumber",
      "firstParentName",
      "groupsIDs",
      "address",
      "age",
      "gender",
      "name",
    ]);

    // This is used to generate group text
    tableRows = replaceValuesInColumn(tableRows, 3, (value) => {
      if (!value || !Array.isArray(value) || value.length === 0) return "-";

      // Get all groups for this student
      const studentGroups = value
        .map((groupId) => finalGroups.find((group) => group?.id === groupId))
        .filter((group) => group); // Remove any undefined groups

      if (studentGroups.length === 0) return "-";

      // Format all groups as "Group Name, Dojo"
      const groupTexts = studentGroups.map(
        (group) => `${group?.name || "-"}, ${group?.dojo || "-"}`
      );

      // Join multiple groups with line breaks
      return groupTexts.join("\n");
    });

    // This is used to generate stats column text
    tableRows = replaceValuesInColumn(tableRows, 0, (value) => {
      if (!value) return "%0";

      let presencePrecent = 0;
      let totalClassesPresent = 0;
      let totalClassesMissed = 0;
      let totalClasses = 0;

      const student = finalStudents.find((student) => student?.id === value);
      if (
        !student?.groupsIDs ||
        !Array.isArray(student.groupsIDs) ||
        student.groupsIDs.length === 0
      )
        return "%0";

      // Get all group logs for all groups this student belongs to
      const allGroupLogs = [];
      student.groupsIDs.forEach((groupId) => {
        const groupLogs = finalPresenceLogs.filter(
          (log) => log?.groupID === groupId?.trim()
        );
        allGroupLogs.push(...groupLogs);
      });

      for (let i = 0; i < allGroupLogs.length; i++) {
        const log = allGroupLogs[i];
        if (!log?.logTime || !isWithinSelectedPeriod(log.logTime)) continue;

        const presenceData = log?.presenceLogDataForFront || [];
        const studentPresence = presenceData.find(
          (data) => data?.studentID === value
        );
        if (studentPresence) {
          if (studentPresence.isPresent) totalClassesPresent++;
          else totalClassesMissed++;
        } else {
          totalClassesMissed++;
        }
      }

      totalClasses = totalClassesPresent + totalClassesMissed;
      if (totalClasses === 0) return "%0";
      presencePrecent = Math.round((totalClassesPresent / totalClasses) * 100);

      return `%${String(presencePrecent).split("").reverse().join("")}`;
    });

    // Stats Text
    doc.setFont("DavidLibre", "normal");
    doc.setFontSize(12);
    doc.text("מספר הספורטאים :", 182, 35, { align: "center" });
    doc.setR2L(false);
    doc.text(`${validStudents.length}`, 160, 35, { align: "center" });

    // Change again the R2L status
    doc.setR2L(true);
    // Add a comment
    doc.setFontSize(10);
    doc.text(
      "הערה: אחוז הנוכחות מחושב על ידי בדיקת נוכחות החניך בכלל האימונים שבוצעו ב-30 הימים האחרונים",
      197,
      45,
      { align: "right" }
    );

    // The table
    doc.setFont("DavidLibre", "normal");
    autoTable(doc, {
      startY: 50,
      head: [tableColumn],
      body: tableRows,
      pageBreak: "auto", // Ensures rows move to the next page properly
      styles: {
        font: "DavidLibre", // Ensure this matches the font you added
        fontSize: 12,
        halign: "right", // Right-align text
        direction: "rtl", // Ensure right-to-left text flow
        textColor: [0, 0, 0], // Set text color to black
        lineColor: [0, 0, 0], // Set grid lines to black
        lineWidth: 0.001, // Adjust thickness of grid lines
      },
      theme: "grid",
      headStyles: {
        fillColor: [232, 232, 232], // Light gray background for header
        textColor: [0, 0, 0], // Ensure header text is black
        lineColor: [0, 0, 0], // Ensure header grid lines are black
      },
      columnStyles: {
        0: {
          halign: "right",
          cellWidth: "auto",
          cellStyles: { direction: "ltr" },
        }, // Ensure phone numbers are LTR
      },
    });

    // Generate and store PDF
    try {
      const pdfBlob = doc.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);

      setPdfBlob(pdfBlob);
      setPdfUrl(pdfUrl);
    } catch (error) {
      console.error("Error generating students report PDF:", error);
      // You might want to show an error message to the user here
    }
  };

  const generatePresenceReport = async () => {
    const doc = new jsPDF();

    // Load and set the Hebrew font
    await loadHebrewFonts(doc);
    doc.setFont("DavidLibre", "bold");

    // Get current date and format it as dd/mm/yyyy hh:mm
    const now = new Date();
    const formattedDate = now.toLocaleDateString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const formattedTime = now.toLocaleTimeString("he-IL", {
      hour: "2-digit",
      minute: "2-digit",
    });

    // Set the document direction to RTL (for Hebrew text)
    doc.setR2L(true);

    // Filter groups based on selection
    let groupsToProcess = finalGroups;
    if (selectedGroups.length > 0) {
      groupsToProcess = finalGroups.filter((group) =>
        selectedGroups.includes(group.id)
      );
    } else if (selectedDojo) {
      groupsToProcess = finalGroups.filter(
        (group) => group.dojo === selectedDojo
      );
    }

    // Sort groups by dojo
    const sortedGroups = [...groupsToProcess].sort((a, b) => {
      // First sort by dojo
      const dojoCompare = a.dojo.localeCompare(b.dojo);
      if (dojoCompare !== 0) return dojoCompare;
      // If same dojo, sort by name
      return a.name.localeCompare(b.name);
    });

    // Helper to split array into chunks
    function chunkArray(array, chunkSize) {
      const results = [];
      for (let i = 0; i < array.length; i += chunkSize) {
        results.push(array.slice(i, i + chunkSize));
      }
      return results;
    }

    // Process each group
    for (let groupIndex = 0; groupIndex < sortedGroups.length; groupIndex++) {
      const group = sortedGroups[groupIndex];

      // Add new page for each group except the first one
      if (groupIndex > 0) {
        doc.addPage();
      }

      // Title
      doc.setFont("DavidLibre", "bold");
      doc.setFontSize(18);
      doc.text(`דוח נוכחות - ${group.name}`, 105, 15, { align: "center" });

      // Dojo subtitle
      doc.setFont("DavidLibre", "normal");
      doc.setFontSize(14);
      doc.text(`סניף: ${group.dojo}`, 105, 25, { align: "center" });

      // Date and Time
      doc.setFontSize(12);
      doc.setR2L(false); // Temporarily disable RTL for date/time
      doc.text(`${formattedDate} ${formattedTime}`, 180, 20, {
        align: "center",
      });
      doc.setR2L(true); // Re-enable RTL for the rest of the content

      // Get all students in this group
      const groupStudents = finalStudents.filter((student) =>
        student?.groupsIDs?.some(
          (groupId) => groupId?.trim() === group?.id?.trim()
        )
      );

      // Get all unique dates from presence logs for this group within the selected period
      const groupLogs = finalPresenceLogs.filter(
        (log) => log?.groupID === group?.id
      );

      // Create an array of all valid logs within the selected period
      const validLogs = groupLogs
        .filter((log) => {
          if (!log?.logTime?.seconds) return false;
          return isWithinSelectedPeriod(log.logTime);
        })
        .sort(
          (a, b) => (b?.logTime?.seconds || 0) - (a?.logTime?.seconds || 0)
        ); // Sort by most recent first

      // Split logs into chunks of 8
      const logChunks = chunkArray(validLogs, 8);

      // Group logs by date for all logs (for all tables)
      const allLogDates = validLogs
        .map((log) => {
          if (!log?.logTime?.seconds) return null;
          const date = new Date(log.logTime.seconds * 1000);
          // Format date in DD/MM format and reverse the numbers to handle RTL
          return date
            .toLocaleDateString("he-IL", {
              day: "2-digit",
              month: "2-digit",
            })
            .split("/")
            .map((part) => part.split("").reverse().join(""))
            .join("/");
        })
        .filter(Boolean);

      // If there are no dates in the selected period, show a message
      if (allLogDates.length === 0) {
        doc.setFont("DavidLibre", "normal");
        doc.setFontSize(14);
        let noDataMessage = "לא התקיימו אימונים בקבוצה בטווח הזמנים שהוגדר";

        if (presenceDateMode === "lastMonth") {
          noDataMessage = "לא התקיימו אימונים בקבוצה זו ב-30 הימים האחרונים";
        } else if (
          presenceDateMode === "customRange" &&
          presenceStartDate &&
          presenceEndDate
        ) {
          const startDateStr = presenceStartDate.toLocaleDateString("he-IL");
          const endDateStr = presenceEndDate.toLocaleDateString("he-IL");
          noDataMessage = `לא התקיימו אימונים בקבוצה זו בין התאריכים ${startDateStr} - ${endDateStr}`;
        }

        doc.text(noDataMessage, 105, 40, {
          align: "center",
        });
      } else {
        // For each chunk of logs (max 8 per table)
        for (let chunkIdx = 0; chunkIdx < logChunks.length; chunkIdx++) {
          const chunkLogs = logChunks[chunkIdx];
          // Get the dates for this chunk
          const chunkDates = chunkLogs
            .map((log) => {
              if (!log?.logTime?.seconds) return null;
              const date = new Date(log.logTime.seconds * 1000);
              return date
                .toLocaleDateString("he-IL", {
                  day: "2-digit",
                  month: "2-digit",
                })
                .split("/")
                .map((part) => part.split("").reverse().join(""))
                .join("/");
            })
            .filter(Boolean);

          // Prepare table data
          const tableColumns = [...chunkDates, "שם"]; // Changed order to show dates on the left
          // Split students into chunks that fit on a page (e.g., 25 per page)
          const studentChunks = chunkArray(groupStudents, 25);

          // Safety check: if no students, skip this chunk
          if (studentChunks.length === 0) continue;

          for (
            let studentChunkIdx = 0;
            studentChunkIdx < studentChunks.length;
            studentChunkIdx++
          ) {
            const studentChunk = studentChunks[studentChunkIdx];
            const tableRows = studentChunk.map((student) => {
              const row = [];
              // Add presence data for each date in this chunk
              chunkLogs.forEach((log) => {
                // Find all logs for this student on this date
                const studentPresence = log?.presenceLogDataForFront?.find(
                  (data) => data?.studentID === student?.id
                );
                row.push(studentPresence?.isPresent ? "V" : "-");
              });
              // Add student name at the end (rightmost column)
              row.push(student?.name || "-");
              return row;
            });

            // Safety check: if no rows, skip this table
            if (tableRows.length === 0) continue;

            // Add the table
            autoTable(doc, {
              startY: studentChunkIdx === 0 && chunkIdx === 0 ? 30 : 35,
              head: [tableColumns],
              body: tableRows,
              pageBreak: "auto",
              styles: {
                font: "DavidLibre",
                fontSize: 10,
                halign: "right",
                direction: "rtl",
                textColor: [0, 0, 0],
                lineColor: [0, 0, 0],
                lineWidth: 0.001,
              },
              theme: "grid",
              headStyles: {
                fillColor: [232, 232, 232],
                textColor: [0, 0, 0],
                lineColor: [0, 0, 0],
                direction: "ltr", // Force LTR for date headers
              },
              columnStyles: {
                [tableColumns.length - 1]: { cellWidth: "auto" }, // Style for the name column
              },
            });
            // If not the last student chunk, add a new page
            if (studentChunkIdx < studentChunks.length - 1) {
              doc.addPage();
              // Re-add group title and subtitle for new page
              doc.setFont("DavidLibre", "bold");
              doc.setFontSize(18);
              doc.text(`דוח נוכחות - ${group.name || "קבוצה"}`, 105, 15, {
                align: "center",
              });
              doc.setFont("DavidLibre", "normal");
              doc.setFontSize(14);
              doc.text(`סניף: ${group.dojo || "לא ידוע"}`, 105, 25, {
                align: "center",
              });
              doc.setFontSize(12);
              doc.setR2L(false);
              doc.text(`${formattedDate} ${formattedTime}`, 180, 20, {
                align: "center",
              });
              doc.setR2L(true);
            }
          }
          // If not the last chunk, add a note and a page break
          if (chunkIdx < logChunks.length - 1) {
            doc.setFontSize(10);
            doc.text(
              `המשך דוח נוכחות לקבוצה זו`,
              197,
              doc.lastAutoTable.finalY + 10,
              { align: "right" }
            );
            doc.addPage();
            // Re-add group title and subtitle for new page
            doc.setFont("DavidLibre", "bold");
            doc.setFontSize(18);
            doc.text(`דוח נוכחות - ${group.name || "קבוצה"}`, 105, 15, {
              align: "center",
            });
            doc.setFont("DavidLibre", "normal");
            doc.setFontSize(14);
            doc.text(`סניף: ${group.dojo || "לא ידוע"}`, 105, 25, {
              align: "center",
            });
            doc.setFontSize(12);
            doc.setR2L(false);
            doc.text(`${formattedDate} ${formattedTime}`, 180, 20, {
              align: "center",
            });
            doc.setR2L(true);
          }
        }

        // Add a note about the time period and log limit
        doc.setFontSize(10);
        let periodNote =
          "הערה: הדוח מציג את נוכחות הספורטאים באימונים שבוצעו בטווח הזמנים שהוגדר";

        if (presenceDateMode === "lastMonth") {
          periodNote =
            "הערה: הדוח מציג את נוכחות הספורטאים באימונים שבוצעו ב-30 הימים האחרונים";
        } else if (
          presenceDateMode === "customRange" &&
          presenceStartDate &&
          presenceEndDate
        ) {
          const startDateStr = presenceStartDate.toLocaleDateString("he-IL");
          const endDateStr = presenceEndDate.toLocaleDateString("he-IL");
          periodNote = `הערה: הדוח מציג את נוכחות הספורטאים באימונים שבוצעו בין התאריכים ${startDateStr} - ${endDateStr}`;
        }

        doc.text(periodNote, 197, doc.lastAutoTable.finalY + 10, {
          align: "right",
        });
      }
    }

    // Generate and store PDF
    try {
      const pdfBlob = doc.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);

      setPdfBlob(pdfBlob);
      setPdfUrl(pdfUrl);
    } catch (error) {
      console.error("Error generating presence report PDF:", error);
      // You might want to show an error message to the user here
    }
  };

  const generateStudentDetailsReport = async () => {
    // Validate required data
    if (!selectedStudent) {
      return;
    }

    // Validate that required props are available
    if (
      !groups ||
      !students ||
      !presenceLogs ||
      !events ||
      !evaluationReports ||
      !evaluationTemplates
    ) {
      return;
    }

    const doc = new jsPDF();

    try {
      // Load and set the Hebrew font
      await loadHebrewFonts(doc);
      doc.setFont("DavidLibre", "bold");

      // Get current date and format it
      const now = new Date();
      const formattedDate = now.toLocaleString("he-IL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });

      // Set the document direction to RTL
      doc.setR2L(true);

      // Title
      doc.setFontSize(18);
      doc.text("פרטי ספורטאי", 105, 20, { align: "center" });

      // Date
      doc.setFont("DavidLibre", "normal");
      doc.setFontSize(12);
      doc.setR2L(false);
      doc.text(formattedDate, 180, 20, { align: "center" });
      doc.setR2L(true);

      // Student Photo
      if (selectedStudent.imageURI) {
        try {
          const img = new Image();
          img.crossOrigin = "anonymous"; // Add cross-origin support

          // Create a promise with timeout
          const imageLoadPromise = new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error("Image load timeout"));
            }, 5000); // 5 second timeout

            img.onload = () => {
              clearTimeout(timeout);
              resolve(img);
            };
            img.onerror = (error) => {
              clearTimeout(timeout);
              reject(error);
            };
          });

          // Set the image source
          img.src = selectedStudent.imageURI;

          // Wait for the image to load or timeout
          const loadedImage = await imageLoadPromise;

          const imgWidth = 40;
          const imgHeight = (loadedImage.height * imgWidth) / loadedImage.width;
          doc.addImage(loadedImage, "JPEG", 150, 30, imgWidth, imgHeight);
        } catch (error) {
          // Continue with report generation even if image fails
        }
      } else {
      }

      // Personal Details Section
      doc.setFont("DavidLibre", "bold");
      doc.setFontSize(14);
      // Use page width for right alignment
      const pageWidth = doc.internal.pageSize.getWidth();
      doc.text("פרטים אישיים", pageWidth - 20, 40, { align: "right" });

      // Format birthday with error handling
      let formattedBirthday = "-";
      try {
        if (
          selectedStudent.birthday &&
          typeof selectedStudent.birthday.seconds === "number"
        ) {
          const birthday = new Date(selectedStudent.birthday.seconds * 1000);
          if (!isNaN(birthday.getTime())) {
            formattedBirthday = birthday.toLocaleDateString("he-IL");
          }
        }
      } catch (error) { }

      // Format creation date with error handling
      let formattedCreatedAt = "-";
      try {
        if (
          selectedStudent.studentCreatedAt &&
          typeof selectedStudent.studentCreatedAt.seconds === "number"
        ) {
          const createdAt = new Date(
            selectedStudent.studentCreatedAt.seconds * 1000
          );
          if (!isNaN(createdAt.getTime())) {
            formattedCreatedAt = createdAt.toLocaleDateString("he-IL");
          }
        }
      } catch (error) { }

      // Personal Details Table (labels without colon, reversed order)
      const personalDetails = [
        [selectedStudent.name || "-", "שם"],
        [reverseStr(selectedStudent.IDnumber) || "-", "תעודת זהות"],
        [selectedStudent.address || "-", "כתובת"],
        [reverseStr(formattedBirthday) || "-", "תאריך לידה"],
        [selectedStudent.gender === "זכר" ? "זכר" : "נקבה", "מין"],
        [selectedStudent.status === "active" ? "פעיל" : "לא פעיל", "סטטוס"],
        [reverseStr(formattedCreatedAt) || "-", "תאריך הצטרפות"],
      ];

      // Safely add special fields
      if (
        selectedStudent.specialFields &&
        typeof selectedStudent.specialFields === "object"
      ) {
        if (selectedStudent.specialFields.beltLevel) {
          personalDetails.push([
            String(selectedStudent.specialFields.beltLevel),
            "דרגת חגורה",
          ]);
        }
        if (selectedStudent.specialFields.weightKG) {
          personalDetails.push([
            reverseStr(String(selectedStudent.specialFields.weightKG)),
            'משקל בק"ג',
          ]);
        }
        if (selectedStudent.specialFields.shirtSize) {
          personalDetails.push([
            reverseStr(String(selectedStudent.specialFields.shirtSize)),
            "מידת חולצה",
          ]);
        }
        if (selectedStudent.specialFields.pantsSize) {
          personalDetails.push([
            reverseStr(String(selectedStudent.specialFields.pantsSize)),
            "מידת מכנסיים",
          ]);
        }
      }
      autoTable(doc, {
        startY: 45,
        head: [["ערך", "שדה"]],
        body: personalDetails,
        theme: "grid",
        styles: {
          font: "DavidLibre",
          fontSize: 12,
          halign: "right",
        },
        headStyles: {
          fillColor: [232, 232, 232],
          textColor: [0, 0, 0],
          fontStyle: "bold",
        },
        columnStyles: {
          0: { cellWidth: 60, halign: "right" },
          1: { cellWidth: 40, halign: "right" },
        },
        tableLineWidth: 0.1,
        margin: { right: 20, left: 90 },
        tableWidth: "auto",
        didParseCell: function (data) {
          // LTR for ID, date, numbers, and any value that starts with \u200E
          if (
            data.section === "body" &&
            (data.row.raw[1] === "תעודת זהות" ||
              data.row.raw[1] === "תאריך לידה" ||
              data.row.raw[1] === "תאריך הצטרפות" ||
              data.row.raw[1] === 'משקל (ק"ג)' ||
              data.row.raw[1] === "מידת חולצה" ||
              data.row.raw[1] === "מידת מכנסיים" ||
              (typeof data.cell.raw === "string" &&
                data.cell.raw.startsWith("\u200E")))
          ) {
            data.cell.styles.fontStyle = "normal";
            data.cell.styles.cellPadding = 2;
            data.cell.styles.halign = "right";
            data.cell.styles.font = "DavidLibre";
            data.cell.styles.textDirection = "ltr";
          }
        },
      });

      // Parent Details Section
      doc.setFont("DavidLibre", "bold");
      doc.setFontSize(14);
      doc.text("פרטי הורים", pageWidth - 20, doc.lastAutoTable.finalY + 10, {
        align: "right",
      });

      const parentDetails = [
        [selectedStudent.firstParentName || "-", "שם הורה ראשון"],
        [
          reverseStr(selectedStudent.firstParentNumber) || "-",
          "טלפון הורה ראשון",
        ],
        [
          reverseStr(selectedStudent.firstParentEmailAddress) || "-",
          "אימייל הורה ראשון",
        ],
      ];

      // Safely add second parent details
      if (selectedStudent.secondParentName) {
        parentDetails.push(
          [String(selectedStudent.secondParentName), "שם הורה שני"],
          [
            reverseStr(selectedStudent.secondParentNumber) || "-",
            "טלפון הורה שני",
          ],
          [
            reverseStr(selectedStudent.secondParentEmailAddress) || "-",
            "אימייל הורה שני",
          ]
        );
      }
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 15,
        head: [["ערך", "שדה"]],
        body: parentDetails,
        theme: "grid",
        styles: {
          font: "DavidLibre",
          fontSize: 12,
          halign: "right",
        },
        headStyles: {
          fillColor: [232, 232, 232],
          textColor: [0, 0, 0],
          fontStyle: "bold",
        },
        columnStyles: {
          0: { cellWidth: 60, halign: "right" },
          1: { cellWidth: 40, halign: "right" },
        },
        tableLineWidth: 0.1,
        margin: { right: 20, left: 90 },
        tableWidth: "auto",
        didParseCell: function (data) {
          // LTR for phone and email fields
          if (
            data.section === "body" &&
            (data.row.raw[1] === "טלפון הורה ראשון" ||
              data.row.raw[1] === "אימייל הורה ראשון" ||
              data.row.raw[1] === "טלפון הורה שני" ||
              data.row.raw[1] === "אימייל הורה שני" ||
              (typeof data.cell.raw === "string" &&
                data.cell.raw.startsWith("\u200E")))
          ) {
            data.cell.styles.fontStyle = "normal";
            data.cell.styles.cellPadding = 2;
            data.cell.styles.halign = "right";
            data.cell.styles.font = "DavidLibre";
            data.cell.styles.textDirection = "ltr";
          }
        },
      });

      // Add new page for statistical data
      doc.addPage();

      // Statistical Data Section
      doc.setFont("DavidLibre", "bold");
      doc.setFontSize(14);
      doc.text("נתונים סטטיסטיים", pageWidth - 20, 20, { align: "right" });

      // Attendance Data Subtitle
      doc.setFontSize(12);
      doc.text("נוכחות באימונים", pageWidth - 20, 30, { align: "right" });

      // Get student's group information with error handling
      let groupNames = [];
      let dojoNames = [];
      let groupLogs = [];
      let trainingSessionsCount = 0;

      try {
        if (
          selectedStudent.groupsIDs &&
          Array.isArray(selectedStudent.groupsIDs) &&
          Array.isArray(finalGroups)
        ) {
          // Get all groups for this student
          selectedStudent.groupsIDs.forEach((groupId) => {
            const studentGroup = finalGroups.find((g) => g?.id === groupId);
            if (studentGroup) {
              groupNames.push(studentGroup.name || "לא ידוע");
              dojoNames.push(studentGroup.dojo || "לא ידוע");
            }
          });
        }

        // Count training sessions in the selected period for all groups
        if (Array.isArray(finalPresenceLogs)) {
          selectedStudent.groupsIDs?.forEach((groupId) => {
            const logs = finalPresenceLogs.filter(
              (log) =>
                log?.groupID === groupId && isWithinSelectedPeriod(log.logTime)
            );
            groupLogs.push(...logs);
          });
          trainingSessionsCount = groupLogs.length;
        }
      } catch (error) { }

      // Get period text in Hebrew
      const periodText = {
        1: "חודש אחרון",
        3: "3 חודשים אחרונים",
        6: "6 חודשים אחרונים",
        12: "שנה אחרונה",
      }[reportPeriod];

      // Add group and training information
      doc.setFont("DavidLibre", "normal");
      doc.setFontSize(11);

      if (groupNames.length === 0) {
        doc.text(`הספורטאי לא משויך לקבוצה`, pageWidth - 20, 40, {
          align: "right",
        });
      } else if (groupNames.length === 1) {
        doc.text(
          `הספורטאי מתאמן בקבוצת ${groupNames[0]} בסניף ${dojoNames[0]}`,
          pageWidth - 20,
          40,
          { align: "right" }
        );
      } else {
        // Multiple groups - show first line with first group
        doc.text(
          `הספורטאי מתאמן בקבוצות: ${groupNames[0]} בסניף ${dojoNames[0]}`,
          pageWidth - 20,
          40,
          { align: "right" }
        );

        // Show additional groups on next line if there are more
        if (groupNames.length > 1) {
          const additionalGroups = groupNames
            .slice(1)
            .map((name, index) => `${name} בסניף ${dojoNames[index + 1]}`)
            .join(", ");
          doc.text(`ובנוסף: ${additionalGroups}`, pageWidth - 20, 47, {
            align: "right",
          });
        }
      }

      // Adjust Y position based on number of groups
      const trainingSessionsY = groupNames.length > 1 ? 54 : 47;
      doc.text(
        `הקבוצות התאמנו ${trainingSessionsCount} פעמים ב-${reportPeriod} ${periodText} האחרונים`,
        pageWidth - 20,
        trainingSessionsY,
        { align: "right" }
      );

      // Calculate attendance statistics with error handling
      let presentCount = 0;
      let attendancePercentage = 0;

      try {
        groupLogs.forEach((log) => {
          if (
            log?.presenceLogDataForFront &&
            Array.isArray(log.presenceLogDataForFront)
          ) {
            const studentPresence = log.presenceLogDataForFront.find(
              (data) => data?.studentID === selectedStudent.id
            );
            // If student is in the log and marked as present, or not in the log at all (count as absent)
            if (studentPresence?.isPresent) {
              presentCount++;
            }
          }
        });

        // Calculate percentage
        attendancePercentage =
          trainingSessionsCount > 0
            ? Math.round((presentCount / trainingSessionsCount) * 100)
            : 0;
      } catch (error) { }

      // Add attendance statistics
      const attendanceY = groupNames.length > 1 ? 61 : 54;
      doc.text(
        `מתוך כלל האימונים, הספורטאי נכח ב-${presentCount} מתוכם )כ-${attendancePercentage} אחוזים(`,
        pageWidth - 20,
        attendanceY,
        { align: "right" }
      );

      // Event Performance Section
      doc.setFont("DavidLibre", "bold");
      doc.setFontSize(12);
      const eventPerformanceY = groupNames.length > 1 ? 77 : 70;
      doc.text("ביצועי ספורטאי באירועים", pageWidth - 20, eventPerformanceY, {
        align: "right",
      });

      // Count events the student participated in with error handling
      let participatedEvents = [];
      try {
        if (
          Array.isArray(events) &&
          evaluationReports &&
          typeof evaluationReports === "object"
        ) {
          participatedEvents = events.filter((event) => {
            if (!event?.id) return false;

            // Get reports for this event
            const eventReports = evaluationReports[event.id] || [];

            // Check if there's at least one report for this student
            return (
              Array.isArray(eventReports) &&
              eventReports.some(
                (report) => report?.studentId === selectedStudent.id
              )
            );
          });
        }
      } catch (error) { }

      // Get period text in Hebrew
      const periodTextForEvents = {
        1: "חודש אחרון",
        3: "3 חודשים אחרונים",
        6: "6 חודשים אחרונים",
        12: "שנה אחרונה",
      }[reportPeriod];

      // Add event participation text
      doc.setFont("DavidLibre", "normal");
      doc.setFontSize(11);
      const eventParticipationY = groupNames.length > 1 ? 87 : 80;
      doc.text(
        `הספורטאי השתתף ב-${participatedEvents.length} אירועים ב${periodTextForEvents}`,
        pageWidth - 20,
        eventParticipationY,
        { align: "right" }
      );

      // Add events table
      if (participatedEvents.length > 0) {
        // Sort events by date (most recent first) with error handling
        let sortedEvents = [];
        let eventsTableData = [];

        try {
          sortedEvents = [...participatedEvents].sort((a, b) => {
            const dateA = a?.timestamp?.seconds || a?.date?.seconds || 0;
            const dateB = b?.timestamp?.seconds || b?.date?.seconds || 0;
            return dateB - dateA;
          });

          // Prepare table data
          eventsTableData = sortedEvents.map((event) => {
            try {
              // Ensure we have a valid timestamp
              const eventTimestamp =
                event?.date?.seconds || event?.timestamp?.seconds;
              if (!eventTimestamp || typeof eventTimestamp !== "number") {
                return ["-", event?.name || "-"];
              }

              // Convert Firestore timestamp to Date
              const eventDate = new Date(eventTimestamp * 1000);
              if (isNaN(eventDate.getTime())) {
                return ["-", event?.name || "-"];
              }

              // Format date in Hebrew locale
              const formattedDate = eventDate.toLocaleDateString("he-IL", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              });

              return [reverseStr(formattedDate), event?.name || "-"];
            } catch (error) {
              console.error("Error processing event:", event, error);
              return ["-", event?.name || "-"];
            }
          });
        } catch (error) {
          console.error("Error sorting events:", error);
          eventsTableData = participatedEvents.map((event) => [
            "-",
            event?.name || "-",
          ]);
        }

        // Add the events table
        const eventsTableY = groupNames.length > 1 ? 97 : 90;
        autoTable(doc, {
          startY: eventsTableY,
          head: [["תאריך", "שם האירוע"]],
          body: eventsTableData,
          theme: "grid",
          styles: {
            font: "DavidLibre",
            fontSize: 12,
            halign: "right",
          },
          headStyles: {
            fillColor: [232, 232, 232],
            textColor: [0, 0, 0],
            fontStyle: "bold",
          },
          columnStyles: {
            0: { cellWidth: 40, halign: "right" },
            1: { cellWidth: 60, halign: "right" },
          },
          tableLineWidth: 0.1,
          margin: { right: 20, left: 90 },
          tableWidth: "auto",
          didParseCell: function (data) {
            // LTR for date fields
            if (data.section === "body" && data.column.index === 0) {
              data.cell.styles.fontStyle = "normal";
              data.cell.styles.cellPadding = 2;
              data.cell.styles.halign = "right";
              data.cell.styles.font = "DavidLibre";
              data.cell.styles.textDirection = "ltr";
            }
          },
        });
      }

      // Get the selected template for statistics
      const templateForStats = selectedTemplate;

      // Add Evaluation Reports Summary Section
      doc.setFont("DavidLibre", "bold");
      doc.setFontSize(12);
      doc.text(
        "סטטיסטיקות משובים",
        pageWidth - 20,
        doc.lastAutoTable.finalY + 20,
        {
          align: "right",
        }
      );

      // If no template is selected, show a message
      if (!templateForStats) {
        doc.setFont("DavidLibre", "normal");
        doc.setFontSize(11);
        const noTemplateMessage =
          evaluationTemplates && evaluationTemplates.length > 0
            ? "לא נבחרה תבנית לסטטיסטיקות. אנא בחר תבנית כדי לראות סטטיסטיקות משובים."
            : "אין תבניות זמינות לחישוב סטטיסטיקה";
        doc.text(
          noTemplateMessage,
          pageWidth - 20,
          doc.lastAutoTable.finalY + 35,
          { align: "right" }
        );
      }

      // Process evaluation reports - filter by time period for statistics with error handling
      let allStudentReports = [];
      let studentReportsForStats = [];
      let studentReportsForFullDisplay = [];

      try {
        if (evaluationReports && typeof evaluationReports === "object") {
          allStudentReports = Object.values(evaluationReports)
            .flat()
            .filter((report) => report?.studentId === selectedStudent.id);

          // For statistics, only use reports from the selected template within the time period
          studentReportsForStats = templateForStats
            ? allStudentReports.filter(
              (report) =>
                report?.templateId === templateForStats.templateId &&
                isEvaluationWithinSelectedPeriod(report?.createdAt)
            )
            : [];

          // For full reports section, use all reports within the time period regardless of template
          studentReportsForFullDisplay = allStudentReports.filter((report) =>
            isEvaluationWithinSelectedPeriod(report?.createdAt)
          );
        }
      } catch (error) { }

      // Split reports by submitterType
      const feedbackTypes = [
        { type: "student", title: "סיכום משובי ספורטאי" },
        { type: "coach", title: "סיכום משובי מאמן" },
      ];

      let currentY = doc.lastAutoTable.finalY + 30;
      let hasAnyData = false;

      // Check if there are any reports for full display (this will be used to determine if we should show the "no data" message)
      const hasAnyReportsForDisplay = studentReportsForFullDisplay.length > 0;

      // Only process statistics if a template is selected
      if (templateForStats) {
        feedbackTypes.forEach(({ type, title }) => {
          // Filter reports for this submitterType
          const filteredReports = studentReportsForStats.filter(
            (r) => r.submitterType === type
          );

          // Group reports by category with error handling
          const categoryData = {};
          filteredReports.forEach((report) => {
            try {
              // Only process reports that use the selected template for statistics
              if (
                report?.templateId === templateForStats.templateId &&
                Array.isArray(report?.data)
              ) {
                report.data.forEach((category) => {
                  try {
                    if (!category?.id || !category?.name) {
                      return;
                    }

                    if (!categoryData[category.id]) {
                      categoryData[category.id] = {
                        name: category.name,
                        parameters: {},
                      };
                    }

                    if (Array.isArray(category.parameters)) {
                      category.parameters.forEach((param) => {
                        try {
                          if (!param?.id || !param?.name) {
                            return;
                          }

                          if (!categoryData[category.id].parameters[param.id]) {
                            categoryData[category.id].parameters[param.id] = {
                              name: param.name,
                              inputType: param.inputType,
                              values: [],
                            };
                          }

                          if (
                            param.inputType === "number" ||
                            param.inputType === "yesOrNo" ||
                            param.inputType === "scale"
                          ) {
                            categoryData[category.id].parameters[
                              param.id
                            ].values.push(param.answerValue);
                          }
                        } catch (error) { }
                      });
                    }
                  } catch (error) { }
                });
              }
            } catch (error) { }
          });

          // Only render if there is any data
          const hasSectionData = Object.values(categoryData).some((cat) =>
            Object.values(cat.parameters).some(
              (param) => param.values.length > 0
            )
          );
          if (!hasSectionData) {
            doc.setFont("DavidLibre", "bold");
            doc.setFontSize(12);
            doc.text(title, pageWidth - 20, currentY, { align: "right" });
            currentY += 8;
            doc.setFont("DavidLibre", "normal");
            doc.setFontSize(11);
            doc.text(
              "לא נמצאה סטטיסטיקה עבור התבנית שנבחרה",
              pageWidth - 20,
              currentY,
              {
                align: "right",
              }
            );
            currentY += 15;
            return;
          }
          // Section title
          doc.setFont("DavidLibre", "bold");
          doc.setFontSize(12);
          doc.text(title, pageWidth - 20, currentY, { align: "right" });
          currentY += 8;

          // For each category, render table as before with error handling
          Object.entries(categoryData).forEach(([categoryId, category]) => {
            try {
              // Filter parameters that have values
              const validParameters = Object.entries(
                category.parameters || {}
              ).filter(
                ([_, param]) =>
                  param?.values &&
                  Array.isArray(param.values) &&
                  param.values.length > 0
              );
              if (validParameters.length === 0) return;

              // Category title
              doc.setFont("DavidLibre", "bold");
              doc.setFontSize(12);
              doc.text(category.name || "קטגוריה", pageWidth - 20, currentY, {
                align: "right",
              });
              currentY += 10;

              // Table data with error handling
              let categoryTableData = validParameters.map(
                ([paramId, param]) => {
                  try {
                    let average, total;
                    const reverseStr = (val) =>
                      val ? String(val).split("").reverse().join("") : val;

                    if (
                      param.inputType === "number" ||
                      param.inputType === "scale"
                    ) {
                      const validValues = param.values.filter(
                        (v) =>
                          v !== null && v !== undefined && !isNaN(Number(v))
                      );
                      if (validValues.length === 0)
                        return ["0", "0", param.name || "שדה"];

                      total = validValues.reduce((a, b) => a + Number(b), 0);
                      average = total / validValues.length;
                      average = Math.round(average * 10) / 10; // Round to 1 decimal place
                      return [
                        reverseStr(total),
                        reverseStr(average),
                        param.name || "שדה",
                      ];
                    } else if (param.inputType === "yesOrNo") {
                      const validValues = param.values.filter(
                        (v) => v === true || v === false
                      );
                      if (validValues.length === 0)
                        return ["0", "0", param.name || "שדה"];

                      const yesCount = validValues.filter(
                        (v) => v === true
                      ).length;
                      const noCount = validValues.length - yesCount;
                      const yesPercent = Math.round(
                        (yesCount / validValues.length) * 100
                      );
                      const noPercent = 100 - yesPercent;
                      // Only reverse the number part, not the percent sign
                      const avgStr = `כן - %${yesPercent} | לא - %${noPercent}`;
                      const totalStr = `כן - ${yesCount} | לא - ${noCount}`;
                      return [totalStr, avgStr, param.name || "שדה"];
                    }
                    return ["", "", param.name || "שדה"];
                  } catch (error) {
                    return ["", "", param?.name || "שדה"];
                  }
                }
              );
              // Render table
              autoTable(doc, {
                startY: currentY,
                head: [["סך הכל", "ממוצע", "שדה"]],
                body: categoryTableData,
                theme: "grid",
                styles: {
                  font: "DavidLibre",
                  fontSize: 12,
                  halign: "right",
                  overflow: "visible",
                },
                headStyles: {
                  fillColor: [232, 232, 232],
                  textColor: [0, 0, 0],
                  fontStyle: "bold",
                },
                columnStyles: {
                  0: {
                    cellWidth: "auto",
                    halign: "right",
                    overflow: "visible",
                  },
                  1: {
                    cellWidth: "auto",
                    halign: "right",
                    overflow: "visible",
                  },
                  2: {
                    cellWidth: "auto",
                    halign: "right",
                    overflow: "visible",
                  },
                },
                tableLineWidth: 0.1,
                margin: { right: 20, left: pageWidth - 20 - 120 },
                tableWidth: "auto",
              });
              currentY = doc.lastAutoTable.finalY + 15;
              hasAnyData = true;
            } catch (error) { }
          });
        });
      } // Close the if (templateForStats) block

      // If no evaluation data exists at all, show a message
      if (!hasAnyData && !hasAnyReportsForDisplay) {
        doc.setFont("DavidLibre", "normal");
        doc.setFontSize(11);
        doc.text(
          "לא נמצאו משובי אירועים לספורטאי זה",
          pageWidth - 20,
          currentY,
          { align: "right" }
        );
      }

      // --- FULL REPORTS SECTION ---
      // Helper to render full reports
      function renderFullReportsSection(reports, sectionTitle) {
        try {
          if (!Array.isArray(reports) || !reports.length) return;

          // Sort reports by timestamp (newest first) with error handling
          const sortedReports = [...reports].sort((a, b) => {
            try {
              const timeA = a?.createdAt?.seconds || 0;
              const timeB = b?.createdAt?.seconds || 0;
              return timeB - timeA;
            } catch (error) {
              return 0;
            }
          });

          // Add a separating page with centered title
          doc.addPage();
          const pageWidth = doc.internal.pageSize.getWidth();
          const pageHeight = doc.internal.pageSize.getHeight();
          doc.setFont("DavidLibre", "bold");
          doc.setFontSize(20);
          doc.text(sectionTitle, pageWidth / 2, pageHeight / 2, {
            align: "center",
          });

          // For each report, render a full page
          sortedReports.forEach((report, idx) => {
            // Add new page for each report
            doc.addPage();

            let y = 30;
            doc.setFont("DavidLibre", "bold");
            doc.setFontSize(16);

            // Report title: use eventId to find event name
            let reportTitle = "משוב";
            const eventObj =
              events && report.eventId
                ? events.find((ev) => ev.id === report.eventId)
                : null;
            if (eventObj && eventObj.name)
              reportTitle = `משוב ${eventObj.name}`;
            else if (report.eventName)
              reportTitle = `משוב ל${report.eventName}`;
            else if (report.templateId)
              reportTitle = `משוב ${report.templateId}`;
            doc.text(reportTitle, pageWidth - 20, y, { align: "right" });
            y += 12;

            // Template name
            if (report.templateId) {
              const template = evaluationTemplates?.find(
                (t) => t.templateId === report.templateId
              );
              doc.setFont("DavidLibre", "normal");
              doc.setFontSize(12);
              doc.text(
                `שם תבנית: ${template?.name || "לא הוגדר"}`,
                pageWidth - 20,
                y,
                {
                  align: "right",
                }
              );
              y += 10;
            }

            // Event time (זמן אירוע)
            if (eventObj && eventObj.date && eventObj.date.seconds) {
              const eventDate = new Date(eventObj.date.seconds * 1000);
              const formattedEventDate = eventDate.toLocaleString("he-IL", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              });
              doc.setFont("DavidLibre", "normal");
              doc.setFontSize(12);
              doc.text(`זמן אירוע: ${formattedEventDate}`, pageWidth - 20, y, {
                align: "right",
              });
              y += 10;
            }

            // Report date (זמן הזנת משוב)
            doc.setFont("DavidLibre", "normal");
            doc.setFontSize(12);
            if (report.createdAt && report.createdAt.seconds) {
              const date = new Date(report.createdAt.seconds * 1000);
              const formattedDate = date.toLocaleString("he-IL", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              });
              doc.text(`זמן הזנת משוב: ${formattedDate}`, pageWidth - 20, y, {
                align: "right",
              });
              y += 10;
            }

            // For each category
            report.data.forEach((category) => {
              // Check if we need a new page before adding category
              if (y > doc.internal.pageSize.getHeight() - 40) {
                doc.addPage();
                y = 30;
              }

              doc.setFont("DavidLibre", "bold");
              doc.setFontSize(13);
              doc.text(category.name, pageWidth - 20, y, { align: "right" });
              y += 8;

              // For each parameter
              category.parameters.forEach((param) => {
                // Check if we need a new page before adding parameter
                if (y > doc.internal.pageSize.getHeight() - 40) {
                  doc.addPage();
                  y = 30;
                }

                let valueStr = "";
                if (param.inputType === "yesOrNo") {
                  valueStr = param.answerValue === true ? "כן" : "לא";
                } else if (
                  param.inputType === "number" ||
                  param.inputType === "scale"
                ) {
                  valueStr = String(param.answerValue || "");
                } else if (param.inputType === "text") {
                  valueStr = param.answerValue || "";
                }

                // Calculate available width for the value (considering margins and param name)
                const pageWidth = doc.internal.pageSize.getWidth();
                const rightMargin = 30;
                const leftMargin = 90;
                const availableWidth = pageWidth - rightMargin - leftMargin;
                const paramNameWidth = doc.getTextWidth(`${param.name}: `);
                const valueWidth = availableWidth - paramNameWidth - 10; // 10px buffer

                // Bold param name
                doc.setFont("DavidLibre", "bold");
                doc.text(`${param.name}:`, pageWidth - rightMargin, y, {
                  align: "right",
                });

                // Handle text wrapping for long answers
                if (param.inputType === "text" && valueStr) {
                  doc.setFont("DavidLibre", "normal");
                  const splitText = doc.splitTextToSize(valueStr, valueWidth);

                  // If text is too long, start on a new line
                  if (splitText.length > 1) {
                    y += 7; // Move to next line
                    splitText.forEach((line, index) => {
                      // Check if we need a new page before adding line
                      if (y > doc.internal.pageSize.getHeight() - 40) {
                        doc.addPage();
                        y = 30;
                      }
                      doc.text(
                        line,
                        pageWidth - rightMargin - paramNameWidth - 5,
                        y,
                        {
                          align: "right",
                        }
                      );
                      y += 7; // Move to next line for each wrapped line
                    });
                    y += 3; // Add extra space after wrapped text
                  } else {
                    // Single line text
                    doc.text(
                      valueStr,
                      pageWidth - rightMargin - paramNameWidth - 5,
                      y,
                      {
                        align: "right",
                      }
                    );
                    y += 7;
                  }
                } else {
                  // Non-text parameters (yes/no, number, scale)
                  doc.setFont("DavidLibre", "normal");
                  doc.text(
                    valueStr,
                    pageWidth - rightMargin - paramNameWidth - 5,
                    y,
                    {
                      align: "right",
                    }
                  );
                  y += 7;
                }
              });
              y += 5; // Add extra space after each category
            });
          });
        } catch (error) {
          console.error("Error in renderFullReportsSection:", error);
        }
      }

      // Render full student feedback reports
      const fullStudentReports = studentReportsForFullDisplay.filter(
        (r) => r.submitterType === "student"
      );
      renderFullReportsSection(fullStudentReports, "משובי ספורטאי מלאים");

      // Render full coach feedback reports
      const fullCoachReports = studentReportsForFullDisplay.filter(
        (r) => r.submitterType === "coach"
      );
      renderFullReportsSection(fullCoachReports, "משובי מאמן מלאים");

      // Generate and store PDF
      const pdfBlob = doc.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);

      setPdfBlob(pdfBlob);
      setPdfUrl(pdfUrl);
    } catch (error) {
      console.error("Error generating report:", error);
    }
  };

  const downloadPdf = () => {
    if (pdfBlob) {
      // Create a link to download the PDF file
      const link = document.createElement("a");
      link.href = URL.createObjectURL(pdfBlob); // Create a download link
      link.download = "report.pdf"; // Set a default filename
      link.click(); // Trigger the download
    }
  };

  const reports_radio = [
    {
      name: `דוח נתוני ספורטאי`,
      value: "4",
      description: `דוח המציג את כלל הנתונים של ספפורטאי בארגון - פרטים אישיים, אירועים שהשתתף בהם וניתוח סטטיסטי של המשובים שהוזנו לגביו`,
      generationFunction: generateStudentDetailsReport,
      icon: <TbReportAnalytics size={20} />,
    },
    {
      name: `דו"ח נוכחות`,
      value: "3",
      description: `דו"ח הנוכחות מציג את נוכחות הספורטאים בכל קבוצה, כאשר כל עמוד מייצג קבוצה אחת. העמודות מייצגות את תאריכי האימונים, והתאים מכילים V לנוכחות או - להיעדרות`,
      generationFunction: generatePresenceReport,
      icon: <TbReportAnalytics size={20} />,
    },
    {
      name: `דו"ח ספורטאים`,
      value: "2",
      description: `דו"ח הספורטאים מציג את כלל הספורטאים בארגון והפרטים שלהם, כולל חישוב סטטיסטי של הנוכחות שלהם באימונים האחרונים על בסיס דיווחי הנוכחות שהוזנו`,
      generationFunction: generateStudentsReport,
      icon: <TbReportAnalytics size={20} />,
    },
    {
      name: `דו"ח קבוצות`,
      value: "1",
      description: `דו"ח הקבוצות מציג את כלל הקבוצות בארגון והפרטים שלהן`,
      generationFunction: generateGroupsReport,
      icon: <TbReportAnalytics size={20} />,
    },
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f8fafc 0%, #e3e9f7 100%)",
        padding: "32px 0",
      }}
    >
      <style>{`
        .modern-reports-card {
          border-radius: 16px !important;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06) !important;
          background: #fff !important;
          transition: box-shadow 0.2s, transform 0.2s;
        }
        /* Removed hover effect from cards */
        /* .modern-reports-card:hover {
          box-shadow: 0 4px 24px #1976d233 !important;
          transform: translateY(-2px) scale(1.01);
        } */
        .modern-section-header-icon {
          font-size: 1.3em;
          margin-left: 8px;
        }
        .modern-add-btn {
          border-radius: 999px !important;
          font-weight: 700 !important;
          font-size: 1.2rem !important;
          padding: 16px 40px !important;
          box-shadow: 0 2px 8px rgba(0,180,80,0.10) !important;
        }
        .modern-modal .modal-content {
          border-radius: 18px !important;
          background: #f8fafc !important;
        }
        .modern-modal .modal-header {
          border-radius: 18px 18px 0 0 !important;
        }
        .modern-modal .modal-footer {
          border-radius: 0 0 18px 18px !important;
        }
        .modern-form-input, .modern-form-input:focus {
          border-radius: 10px !important;
          box-shadow: 0 1px 4px rgba(0,0,0,0.04) !important;
          border: 1px solid #e0e0e0 !important;
          background: #fff !important;
        }
        .modern-reports-table {
          border-radius: 16px !important;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06) !important;
          background: #fff !important;
          padding: 16px;
        }
        .modern-toggle-btn {
          border-radius: 999px !important;
          font-weight: 700 !important;
          font-size: 1.1rem !important;
          padding: 12px 24px !important;
          margin: 0 4px;
          transition: all 0.2s;
        }
        .modern-toggle-btn.selected, .modern-toggle-btn:checked, .modern-toggle-btn.active {
          background: linear-gradient(135deg, #1976d2 60%, #03a600 100%) !important;
          color: #fff !important;
          box-shadow: 0 2px 8px #1976d233 !important;
        }
        .modern-toggle-btn:not(.selected):not(:checked):not(.active) {
          background: #f8fafc !important;
          color: #1976d2 !important;
          border: 1px solid #dee2e6 !important;
        }
        .modern-action-btn {
          border-radius: 999px !important;
          font-weight: 700 !important;
          font-size: 1.1rem !important;
          padding: 12px 32px !important;
          box-shadow: 0 2px 8px rgba(0,180,80,0.10) !important;
        }
        .modern-action-btn svg {
          margin-left: 8px;
        }
        .modern-pdf-preview-card {
          border-radius: 16px !important;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06) !important;
          background: #fff !important;
          margin-top: 32px;
        }
        .modern-search-dropdown {
          z-index: 1000;
          max-height: 200px;
          overflow-y: auto;
          direction: rtl;
          background: #fff;
          border: 1px solid #eee;
          border-radius: 0 0 12px 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
        .modern-search-dropdown .dropdown-item {
          cursor: pointer;
          padding: 12px 16px;
          border-bottom: 1px solid #eee;
          transition: background 0.15s;
        }
        .modern-search-dropdown .dropdown-item:hover {
          background: #f8fafc;
        }
        .modern-selected-student {
          background: #f8fafc;
          border-radius: 10px;
          padding: 10px 16px;
          margin-top: 8px;
          align-items: flex-start;
          justify-content: flex-start;
          border: 1px solid #e0e0e0;
        }
        
        /* DatePicker RTL styles for presence report */
        .react-datepicker-wrapper {
          width: 100%;
        }
        .react-datepicker__input-container input {
          width: 100%;
          padding: 12px 16px;
          border-radius: 10px;
          border: 1px solid #e0e0e0;
          background: #fff;
          font-size: 14px;
          transition: all 0.2s;
          text-align: right !important;
          direction: rtl !important;
        }
        .react-datepicker__input-container input:focus {
          outline: none;
          border-color: #1976d2;
          box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
        }
        .react-datepicker {
          direction: rtl;
          text-align: right;
          font-family: inherit;
        }
        .react-datepicker__header {
          direction: rtl !important;
          text-align: center !important;
        }
        .react-datepicker__current-month {
          direction: rtl !important;
          text-align: center !important;
          unicode-bidi: plaintext !important;
          width: 100%;
          display: block;
          font-weight: bold;
        }
        .react-datepicker__close-icon {
          right: auto !important;
          left: 8px !important;
        }
        .react-datepicker__input-container input {
          padding-left: 32px !important;
          padding-right: 8px !important;
        }
        
        /* Multiple select styling for group selection */
        .modern-form-input[multiple] {
          min-height: 120px !important;
          padding: 8px 12px !important;
        }
        .modern-form-input[multiple] option {
          padding: 8px 12px !important;
          margin: 2px 0 !important;
          border-radius: 6px !important;
        }
        .modern-form-input[multiple] option:checked {
          background: linear-gradient(135deg, #1976d2 60%, #03a600 100%) !important;
          color: white !important;
        }
        .modern-form-input[multiple] option:hover {
          background: #f8fafc !important;
        }
        
        /* Clickable group styling for group selection */
        .selected-group {
          background: linear-gradient(135deg, #1976d2 60%, #03a600 100%) !important;
          color: #fff !important;
          font-weight: 600 !important;
          box-shadow: 0 2px 4px rgba(25, 118, 210, 0.2) !important;
        }
        .selected-group:hover {
          background: linear-gradient(135deg, #1565c0 60%, #028e00 100%) !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 8px rgba(25, 118, 210, 0.3) !important;
        }
      `}</style>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 16px" }}>
        <div
          className="modern-reports-card"
          style={{ marginBottom: 40, padding: 32 }}
        >
          <h2
            className="mb-4 text-center"
            style={{
              fontWeight: 800,
              fontSize: "2.5rem",
              color: "#1976d2",
              letterSpacing: 1,
            }}
          >
            מחולל דוחות
          </h2>

          <div className="d-flex justify-content-center mb-4">
            <ButtonGroup className="d-flex flex-wrap justify-content-center">
              {reports_radio.map((radio, idx) => (
                <ToggleButton
                  key={idx}
                  id={`radio-${idx}`}
                  type="radio"
                  variant="light"
                  name="radio"
                  value={radio.value}
                  checked={reportType === radio.value}
                  onChange={(e) => setReportType(e.currentTarget.value)}
                  className={`modern-toggle-btn${reportType === radio.value ? " selected" : ""
                    } py-3 d-flex align-items-center justify-content-center gap-2`}
                  style={{ flex: 1, minWidth: 200, margin: 4 }}
                >
                  {radio.icon}
                  <strong>{radio.name}</strong>
                </ToggleButton>
              ))}
            </ButtonGroup>
          </div>

          <div
            className="modern-reports-card bg-light mb-4"
            style={{ padding: 32, border: "none", boxShadow: "none" }}
          >
            <h4
              className="mb-3"
              style={{ color: "#1976d2", textAlign: "right", fontWeight: 700 }}
            >
              {generateReportTitle(reportType)}
            </h4>
            <p
              style={{
                direction: "rtl",
                marginBottom: "1rem",
                color: "#666",
                lineHeight: "1.6",
                fontSize: "1.1rem",
              }}
            >
              {generateReportDescription(reportType)}
            </p>

            {/* Date selection for presence report */}
            {reportType === "3" && (
              <div className="mb-4">
                {/* Group selection section */}
                <Form.Group className="mb-4">
                  <Form.Label
                    style={{
                      textAlign: "right",
                      display: "block",
                      direction: "rtl",
                      fontWeight: 600,
                      marginBottom: "12px",
                    }}
                  >
                    בחירת קבוצות:
                  </Form.Label>

                  {/* Dojo selection */}
                  <div className="mb-3">
                    <Form.Label
                      style={{
                        textAlign: "right",
                        display: "block",
                        direction: "rtl",
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        marginBottom: "8px",
                      }}
                    >
                      בחר סניף:
                    </Form.Label>
                    <Form.Select
                      value={selectedDojo}
                      onChange={(e) => {
                        setSelectedDojo(e.target.value);
                        setSelectedGroups([]); // Reset selected groups when dojo changes
                      }}
                      style={{ direction: "rtl", borderRadius: 10 }}
                      className="modern-form-input"
                    >
                      <option value="">כל הסניפים</option>
                      {uniqueDojos.map((dojo) => (
                        <option key={dojo} value={dojo}>
                          {dojo}
                        </option>
                      ))}
                    </Form.Select>

                    {/* Show message when "all dojos" is selected */}
                    {!selectedDojo && (
                      <Form.Text
                        className="text-muted"
                        style={{
                          direction: "rtl",
                          display: "block",
                          marginTop: "8px",
                        }}
                      >
                        כלל הקבוצות מכלל הסניפים יופיעו בדוח
                      </Form.Text>
                    )}
                  </div>

                  {/* Group selection - only show when specific dojo is selected */}
                  {selectedDojo && (
                    <div className="mb-3">
                      <Form.Label
                        style={{
                          textAlign: "right",
                          display: "block",
                          direction: "rtl",
                          fontWeight: 600,
                          fontSize: "0.9rem",
                          marginBottom: "8px",
                        }}
                      >
                        בחר קבוצות:
                      </Form.Label>
                      <div
                        style={{
                          direction: "rtl",
                          borderRadius: 10,
                          minHeight: "120px",
                          maxHeight: "200px",
                          overflowY: "auto",
                          border: "1px solid #e0e0e0",
                          padding: "8px 12px",
                          backgroundColor: "#fff",
                          opacity: 1,
                          pointerEvents: "auto",
                        }}
                        className="modern-form-input"
                      >
                        {groupsInSelectedDojo.map((group) => (
                          <div
                            key={group.id}
                            onClick={() => {
                              if (selectedGroups.includes(group.id)) {
                                setSelectedGroups(
                                  selectedGroups.filter((id) => id !== group.id)
                                );
                              } else {
                                setSelectedGroups([
                                  ...selectedGroups,
                                  group.id,
                                ]);
                              }
                            }}
                            style={{
                              padding: "8px 12px",
                              marginBottom: "6px",
                              borderRadius: "6px",
                              cursor: "pointer",
                              backgroundColor: selectedGroups.includes(group.id)
                                ? "linear-gradient(135deg, #1976d2 60%, #03a600 100%)"
                                : "transparent",
                              color: selectedGroups.includes(group.id)
                                ? "#fff"
                                : "#333",
                              fontWeight: selectedGroups.includes(group.id)
                                ? "600"
                                : "400",
                              transition: "all 0.2s ease",
                              textAlign: "right",
                              userSelect: "none",
                            }}
                            className={
                              selectedGroups.includes(group.id)
                                ? "selected-group"
                                : ""
                            }
                          >
                            {group.name}
                          </div>
                        ))}
                        {groupsInSelectedDojo.length === 0 && (
                          <div
                            style={{
                              textAlign: "center",
                              color: "#666",
                              padding: "20px",
                            }}
                          >
                            אין קבוצות בסניף זה
                          </div>
                        )}
                      </div>
                      <Form.Text
                        className="text-muted"
                        style={{
                          direction: "rtl",
                          display: "block",
                          marginTop: "4px",
                        }}
                      >
                        {selectedGroups.length > 0
                          ? `נבחרו ${selectedGroups.length} קבוצות מהסניף ${selectedDojo}`
                          : "בחר קבוצות מהסניף שנבחר (או השאר ריק לכל הקבוצות בסניף)"}
                      </Form.Text>
                    </div>
                  )}
                </Form.Group>

                {/* Date selection section */}
                <Form.Group className="mb-3">
                  <Form.Label
                    style={{
                      textAlign: "right",
                      display: "block",
                      direction: "rtl",
                      fontWeight: 600,
                      marginBottom: "12px",
                    }}
                  >
                    בחירת טווח תאריכים:
                  </Form.Label>
                  <div
                    className="d-flex gap-3 mb-3"
                    style={{ alignItems: "center", justifyContent: "center" }}
                  >
                    <Button
                      variant={
                        presenceDateMode === "customRange"
                          ? "primary"
                          : "outline-primary"
                      }
                      onClick={() => setPresenceDateMode("customRange")}
                      className={`modern-toggle-btn${presenceDateMode === "customRange" ? " selected" : ""
                        }`}
                      style={{ flex: 1 }}
                    >
                      טווח מותאם אישית
                    </Button>
                    <Button
                      variant={
                        presenceDateMode === "lastMonth"
                          ? "primary"
                          : "outline-primary"
                      }
                      onClick={() => setPresenceDateMode("lastMonth")}
                      className={`modern-toggle-btn${presenceDateMode === "lastMonth" ? " selected" : ""
                        }`}
                      style={{ flex: 1 }}
                    >
                      חודש אחרון
                    </Button>
                  </div>

                  {presenceDateMode === "customRange" && (
                    <div
                      className="row g-3"
                      style={{ justifyContent: "center", alignItems: "center" }}
                    >
                      <div className="col-md-3">
                        <Form.Label
                          style={{
                            textAlign: "right",
                            display: "block",
                            direction: "rtl",
                            fontWeight: 600,
                            fontSize: "0.9rem",
                          }}
                        >
                          תאריך סיום:
                        </Form.Label>
                        <DatePicker
                          selected={presenceEndDate}
                          onChange={(date) => setPresenceEndDate(date)}
                          selectsEnd
                          startDate={presenceStartDate}
                          endDate={presenceEndDate}
                          minDate={
                            presenceStartDate
                              ? new Date(
                                presenceStartDate.getTime() +
                                24 * 60 * 60 * 1000
                              )
                              : null
                          }
                          maxDate={new Date()}
                          dateFormat="dd/MM/yyyy"
                          className="form-control modern-form-input"
                          placeholderText="בחר תאריך סיום"
                          isClearable
                          locale={he}
                          popperPlacement="bottom-start"
                          showTodayButton={true}
                          todayButton="היום"
                          style={{
                            textAlign: "right",
                            direction: "rtl",
                            width: "100%",
                          }}
                        />
                      </div>
                      <div className="col-md-3">
                        <Form.Label
                          style={{
                            textAlign: "right",
                            display: "block",
                            direction: "rtl",
                            fontWeight: 600,
                            fontSize: "0.9rem",
                          }}
                        >
                          תאריך התחלה:
                        </Form.Label>
                        <DatePicker
                          selected={presenceStartDate}
                          onChange={(date) => {
                            setPresenceStartDate(date);
                            // Reset end date if it's now before the new start date
                            if (
                              date &&
                              presenceEndDate &&
                              date > presenceEndDate
                            ) {
                              setPresenceEndDate(null);
                            }
                          }}
                          selectsStart
                          startDate={presenceStartDate}
                          endDate={presenceEndDate}
                          maxDate={new Date()}
                          dateFormat="dd/MM/yyyy"
                          className="form-control modern-form-input"
                          placeholderText="בחר תאריך התחלה"
                          isClearable
                          locale={he}
                          popperPlacement="bottom-start"
                          showTodayButton={true}
                          todayButton="היום"
                          style={{
                            textAlign: "right",
                            direction: "rtl",
                            width: "100%",
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {presenceDateMode === "customRange" && (
                    <Form.Text
                      className="text-muted"
                      style={{
                        direction: "rtl",
                        display: "block",
                        marginTop: "8px",
                      }}
                    >
                      {presenceStartDate && presenceEndDate
                        ? `הדוח יציג אימונים בין התאריכים ${presenceStartDate.toLocaleDateString(
                          "he-IL"
                        )} - ${presenceEndDate.toLocaleDateString("he-IL")}`
                        : "אנא בחר תאריכי התחלה וסיום"}
                    </Form.Text>
                  )}
                </Form.Group>
              </div>
            )}
            {reportType === "4" && (
              <div className="mb-3 position-relative">
                <Form.Group>
                  <Form.Label
                    style={{
                      textAlign: "right",
                      display: "block",
                      direction: "rtl",
                      fontWeight: 600,
                    }}
                  >
                    בחר ספורטאי:
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="חיפוש ספורטאי..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    style={{
                      direction: "rtl",
                      borderRadius: 10,
                      marginBottom: 0,
                    }}
                    className="modern-form-input"
                  />
                  {showDropdown && searchQuery && (
                    <div className="modern-search-dropdown position-absolute" style={{ width: "100%" }}>
                      {filteredStudents.length > 0 ? (
                        filteredStudents.map((student) => (
                          <div
                            key={student.id}
                            className="dropdown-item"
                            onClick={() => {
                              setSelectedStudent(student);
                              setSearchQuery(student.name);
                              setShowDropdown(false);
                            }}
                          >
                            {student.name}
                            {student.groupsIDs &&
                              student.groupsIDs.length > 0 && (
                                <div
                                  className="text-muted"
                                  style={{ fontSize: "0.85em" }}
                                >
                                  {(() => {
                                    const studentGroups = student.groupsIDs
                                      .map((groupId) =>
                                        finalGroups.find(
                                          (g) => g.id === groupId
                                        )
                                      )
                                      .filter((group) => group);

                                    if (studentGroups.length === 0) return "";
                                    if (studentGroups.length === 1) {
                                      return `קבוצת ${studentGroups[0].name} בסניף ${studentGroups[0].dojo}`;
                                    }
                                    return `${studentGroups.length} קבוצות`;
                                  })()}
                                </div>
                              )}
                          </div>
                        ))
                      ) : (
                        <div className="dropdown-item text-muted">
                          לא נמצאו תוצאות
                        </div>
                      )}
                    </div>
                  )}
                  {selectedStudent && (
                    <div
                      className="modern-selected-student d-flex"
                      style={{ flexDirection: "row-reverse" }}
                    >
                      <div>
                        <strong>{selectedStudent.name}</strong>
                        {selectedStudent.groupsIDs &&
                          selectedStudent.groupsIDs.length > 0 && (
                            <div
                              className="text-muted"
                              style={{ fontSize: "0.9em" }}
                            >
                              {(() => {
                                const studentGroups = selectedStudent.groupsIDs
                                  .map((groupId) =>
                                    finalGroups.find((g) => g.id === groupId)
                                  )
                                  .filter((group) => group);

                                if (studentGroups.length === 0) return "";
                                if (studentGroups.length === 1) {
                                  return `קבוצת ${studentGroups[0].name} בסניף ${studentGroups[0].dojo}`;
                                }
                                return `${studentGroups.length} קבוצות`;
                              })()}
                            </div>
                          )}
                      </div>
                    </div>
                  )}
                </Form.Group>

                {/* Add time period selector */}
                <Form.Group className="mt-3">
                  <Form.Label
                    style={{
                      textAlign: "right",
                      display: "block",
                      direction: "rtl",
                      fontWeight: 600,
                    }}
                  >
                    תקופת הדוח:
                  </Form.Label>
                  <Form.Select
                    value={reportPeriod}
                    onChange={(e) => setReportPeriod(e.target.value)}
                    style={{ direction: "rtl", borderRadius: 10 }}
                    className="modern-form-input"
                  >
                    <option value="1">חודש אחרון</option>
                    <option value="3">3 חודשים אחרונים</option>
                    <option value="6">6 חודשים אחרונים</option>
                    <option value="12">שנה אחרונה</option>
                  </Form.Select>
                </Form.Group>

                {/* Add template selector - only show if templates are available */}
                {evaluationTemplates && evaluationTemplates.length > 0 && (
                  <Form.Group className="mt-3">
                    <Form.Label
                      style={{
                        textAlign: "right",
                        display: "block",
                        direction: "rtl",
                        fontWeight: 600,
                      }}
                    >
                      תבנית לסטטיסטיקות:
                    </Form.Label>
                    <Form.Select
                      value={selectedTemplate?.templateId || ""}
                      onChange={(e) => {
                        const template = evaluationTemplates?.find(
                          (t) => t.templateId === e.target.value
                        );
                        setSelectedTemplate(template || null);
                      }}
                      style={{ direction: "rtl", borderRadius: 10 }}
                      className="modern-form-input"
                    >
                      <option value="">בחר תבנית...</option>
                      {evaluationTemplates
                        ?.filter((template) => !template.isArchived)
                        ?.map((template) => (
                          <option
                            key={template.templateId}
                            value={template.templateId}
                          >
                            {template.name || template.templateId}
                          </option>
                        ))}
                    </Form.Select>
                    {selectedTemplate && (
                      <Form.Text
                        className="text-muted"
                        style={{ direction: "rtl" }}
                      >
                        תבנית זו תשמש להצגת סטטיסטיקה שמסכמת את משובי הספורטאי
                      </Form.Text>
                    )}
                  </Form.Group>
                )}
              </div>
            )}
            <div className="d-flex justify-content-end gap-3 mt-4">
              <Button
                variant="primary"
                onClick={() => generateCurrentReport(reportType)}
                className="modern-action-btn d-flex align-items-center gap-2"
                style={{
                  alignContent: "center",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                disabled={
                  reportType === "3" &&
                  presenceDateMode === "customRange" &&
                  (!presenceStartDate || !presenceEndDate)
                }
              >
                <TbReportAnalytics size={20} />
                צור דוח
              </Button>
              {pdfBlob && (
                <Button
                  variant="outline-primary"
                  onClick={downloadPdf}
                  className="modern-action-btn d-flex align-items-center gap-2"
                  style={{
                    alignContent: "center",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <BsDownload size={20} />
                  PDF הורד
                </Button>
              )}
            </div>
          </div>
          {pdfUrl && (
            <div className="modern-pdf-preview-card">
              <iframe
                src={pdfUrl}
                title="Generated PDF"
                width="100%"
                height="600px"
                style={{ border: "none", borderRadius: "0.375rem" }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReportsPane;
